<script setup>
// Cadastro de profissional com a grade semanal de atendimento (ver AGENDA.md).
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import agendaAPI from 'dashboard/api/agenda';
import { useAgendaData } from '../composables/useAgendaData';
import { timeToMinutes } from '../helpers/agendaTime';

const { t } = useI18n();
const { specialties, replaceProfessional } = useAgendaData();

// Segunda a domingo (0 = domingo, como no servidor).
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];
const WORKDAYS = [1, 2, 3, 4, 5];
const COLORS = [
  '#14B8A6',
  '#0EA5E9',
  '#6366F1',
  '#A855F7',
  '#EC4899',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#64748B',
];
const TIME_FORMAT = /^\d{2}:\d{2}$/;

const dialog = ref(null);
const professional = ref(null);
const form = ref({});
const grid = ref({});
const isSaving = ref(false);
const error = ref('');

const emptyGrid = () => Object.fromEntries(WEEKDAYS.map(day => [day, []]));

const open = existing => {
  professional.value = existing || null;
  error.value = '';
  form.value = {
    name: existing?.name || '',
    specialty: existing?.specialty || '',
    duration_minutes: String(existing?.duration_minutes || 30),
    color: existing?.color || COLORS[0],
    active: existing ? existing.active : true,
  };
  const value = emptyGrid();
  (existing?.working_periods || []).forEach(period => {
    value[period.weekday].push({ start: period.start, end: period.end });
  });
  grid.value = value;
  dialog.value?.open();
};

const addPeriod = day => {
  const periods = grid.value[day];
  const last = periods[periods.length - 1];
  periods.push(last ? { start: '14:00', end: '18:00' } : { start: '08:00', end: '12:00' });
};

const removePeriod = (day, index) => grid.value[day].splice(index, 1);

const copyToWorkdays = () => {
  const source = grid.value[1].map(period => ({ ...period }));
  WORKDAYS.forEach(day => {
    grid.value[day] = source.map(period => ({ ...period }));
  });
};

const gridValid = computed(() =>
  WEEKDAYS.every(day => {
    const periods = grid.value[day] || [];
    if (!periods.every(p => TIME_FORMAT.test(p.start) && TIME_FORMAT.test(p.end))) {
      return false;
    }
    const sorted = periods
      .map(p => ({ start: timeToMinutes(p.start), end: timeToMinutes(p.end) }))
      .sort((a, b) => a.start - b.start);
    return sorted.every(
      (period, index) =>
        period.end > period.start && (index === 0 || period.start >= sorted[index - 1].end)
    );
  })
);

const durationValid = computed(() => {
  const value = Number(form.value.duration_minutes);
  return Number.isInteger(value) && value >= 5 && value <= 480;
});

const canSave = computed(
  () =>
    form.value.name?.trim() &&
    form.value.specialty?.trim() &&
    durationValid.value &&
    gridValid.value
);

const save = async () => {
  if (!canSave.value || isSaving.value) return;
  isSaving.value = true;
  error.value = '';
  const payload = {
    name: form.value.name.trim(),
    specialty: form.value.specialty.trim(),
    duration_minutes: Number(form.value.duration_minutes),
    color: form.value.color,
    active: form.value.active,
    working_periods: WEEKDAYS.flatMap(day =>
      grid.value[day].map(period => ({ weekday: day, ...period }))
    ),
  };
  try {
    const { data } = professional.value
      ? await agendaAPI.updateProfessional(professional.value.id, payload)
      : await agendaAPI.createProfessional(payload);
    replaceProfessional(data);
    useAlert(t('AGENDA.PROFESSIONALS.FORM.SAVED'));
    dialog.value?.close();
  } catch (requestError) {
    error.value =
      requestError?.response?.data?.message || t('AGENDA.PROFESSIONALS.FORM.ERROR');
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="
      professional
        ? t('AGENDA.PROFESSIONALS.FORM.EDIT_TITLE')
        : t('AGENDA.PROFESSIONALS.FORM.NEW_TITLE')
    "
    :confirm-button-label="t('AGENDA.PROFESSIONALS.FORM.SAVE')"
    :disable-confirm-button="!canSave"
    :is-loading="isSaving"
    width="2xl"
    position="top"
    overflow-y-auto
    @confirm="save"
  >
    <div class="flex flex-col gap-5" data-testid="agenda-professional-dialog">
      <div class="grid gap-3 sm:grid-cols-2">
        <Input
          v-model="form.name"
          :label="t('AGENDA.PROFESSIONALS.FORM.NAME')"
          :placeholder="t('AGENDA.PROFESSIONALS.FORM.NAME_PLACEHOLDER')"
          autofocus
        />
        <Input
          v-model="form.specialty"
          :label="t('AGENDA.PROFESSIONALS.FORM.SPECIALTY')"
          :placeholder="t('AGENDA.PROFESSIONALS.FORM.SPECIALTY_PLACEHOLDER')"
          list="agenda-specialties"
        />
        <datalist id="agenda-specialties">
          <option v-for="item in specialties" :key="item" :value="item" />
        </datalist>
        <Input
          v-model="form.duration_minutes"
          type="number"
          max="480"
          :label="t('AGENDA.PROFESSIONALS.FORM.DURATION')"
          :message-type="durationValid ? 'info' : 'error'"
        />
        <div class="flex flex-col gap-2">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('AGENDA.PROFESSIONALS.FORM.COLOR') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="color in COLORS"
              :key="color"
              type="button"
              class="rounded-full size-7 outline-offset-2"
              :class="form.color === color ? 'outline outline-2 outline-n-slate-12' : ''"
              :style="{ backgroundColor: color }"
              :aria-label="color"
              @click="form.color = color"
            />
          </div>
        </div>
      </div>

      <div
        v-if="professional"
        class="flex items-start justify-between gap-4 px-4 py-3 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
      >
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('AGENDA.PROFESSIONALS.FORM.ACTIVE') }}
          </span>
          <span class="text-label-small text-n-slate-11">
            {{ t('AGENDA.PROFESSIONALS.FORM.ACTIVE_HELP') }}
          </span>
        </div>
        <Switch v-model="form.active" />
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('AGENDA.PROFESSIONALS.FORM.GRID') }}
          </span>
          <span class="text-label-small text-n-slate-11">
            {{ t('AGENDA.PROFESSIONALS.FORM.GRID_HELP') }}
          </span>
        </div>
        <ul class="flex flex-col divide-y divide-n-weak" data-testid="agenda-grid-editor">
          <li
            v-for="day in WEEKDAYS"
            :key="day"
            class="flex flex-wrap items-start gap-3 py-2.5"
          >
            <span class="w-20 pt-1.5 text-sm font-medium text-n-slate-12 shrink-0">
              {{ t(`AGENDA.WEEKDAYS.${day}`) }}
            </span>
            <div class="flex flex-col flex-1 gap-2 min-w-[14rem]">
              <span
                v-if="!grid[day].length"
                class="pt-1.5 text-sm text-n-slate-10"
              >
                {{ t('AGENDA.PROFESSIONALS.FORM.CLOSED') }}
              </span>
              <div
                v-for="(period, index) in grid[day]"
                :key="index"
                class="flex items-center gap-2"
              >
                <input
                  v-model="period.start"
                  type="time"
                  step="300"
                  class="!mb-0 w-32 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak border-0 text-n-slate-12 tabular-nums"
                  :aria-label="`${t(`AGENDA.WEEKDAYS.${day}`)} ${index + 1}`"
                />
                <span class="text-n-slate-10">–</span>
                <input
                  v-model="period.end"
                  type="time"
                  step="300"
                  class="!mb-0 w-32 h-8 px-2 text-sm rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak border-0 text-n-slate-12 tabular-nums"
                  :aria-label="`${t(`AGENDA.WEEKDAYS.${day}`)} ${index + 1}`"
                />
                <Button
                  type="button"
                  icon="i-lucide-x"
                  variant="ghost"
                  color="slate"
                  size="xs"
                  :aria-label="t('AGENDA.PROFESSIONALS.FORM.REMOVE_PERIOD')"
                  @click="removePeriod(day, index)"
                />
              </div>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                icon="i-lucide-plus"
                :label="t('AGENDA.PROFESSIONALS.FORM.ADD_PERIOD')"
                variant="ghost"
                color="slate"
                size="xs"
                @click="addPeriod(day)"
              />
              <Button
                v-if="day === 1 && grid[1].length"
                type="button"
                icon="i-lucide-copy"
                :label="t('AGENDA.PROFESSIONALS.FORM.COPY_TO_WEEKDAYS')"
                variant="ghost"
                color="slate"
                size="xs"
                @click="copyToWorkdays"
              />
            </div>
          </li>
        </ul>
        <p v-if="!gridValid" class="mb-0 text-label-small text-n-ruby-11">
          {{ t('AGENDA.PROFESSIONALS.FORM.INVALID_PERIOD') }}
        </p>
      </div>

      <p
        v-if="error"
        class="px-4 py-3 mb-0 text-sm rounded-xl bg-n-ruby-2 text-n-ruby-11"
      >
        {{ error }}
      </p>
    </div>
  </Dialog>
</template>
